import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { boardTrips } from "@/lib/tripsFetch";
import { planByDay } from "@/lib/assignPlan";
import { toast } from "sonner";
import { Panel, PageHeader, Loading } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import ReassignDialog from "@/components/portal/ReassignDialog";
import { Loader2, Sparkles, Inbox, RefreshCw } from "lucide-react";
import { fmtDateTime } from "@/lib/time";
import { isOpenRequest as isOpen, REQUESTS_CHANGED } from "@/lib/assignRequests";
import { useLiveRefresh } from "@/lib/liveRefresh";

const RequestRow = ({ t, pick, miss, onAssigned, onManual }) => {
  const [busy, setBusy] = useState(false);
  const r = t.assign_request;
  const oneTap = async () => {
    setBusy(true);
    try {
      await fleetApi.post("/trips/assign-confirm", { assignments: [pick] });
      toast.success(`${t.external_trip_id} assigned to ${pick.driver_name || "driver"}.`);
      onAssigned(t.id);
    } catch (e) { toast.error(fleetError(e, "Couldn't assign this trip.")); setBusy(false); }
  };
  return (
    <li className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center" data-testid={`assign-request-${t.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-white"><Link to={`/portal/trips/${t.id}`} className="hover:text-blue-300 hover:underline" data-testid={`assign-request-open-${t.id}`}>{t.rider_name}</Link>
          <span className="ml-2 font-mono-data text-[11px] font-normal text-slate-500">{t.external_trip_id} · {fmtDateTime(t.scheduled_pickup)}</span>
        </p>
        <p className="truncate text-xs text-slate-400">{t.pickup_address} → {t.dropoff_name || t.dropoff_address}</p>
        <p className="mt-1 text-xs text-amber-300" data-testid={`assign-request-by-${t.id}`}>
          Asked by {r.by || "a team member"}{r.by_role ? ` (${r.by_role})` : ""} · {fmtDateTime(r.at)}{r.note ? ` — “${r.note}”` : ""}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {pick ? (
          <Button size="sm" disabled={busy} onClick={oneTap} className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`assign-request-ai-${t.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Assign to {pick.driver_name || pick.driver_id}
          </Button>
        ) : (
          <span className="text-xs text-slate-500" data-testid={`assign-request-miss-${t.id}`}>{miss || "No AI match"}</span>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => onManual(t)} className="text-blue-300 hover:bg-slate-800" data-testid={`assign-request-manual-${t.id}`}>Pick driver</Button>
      </div>
    </li>
  );
};

export default function RequestsPage() {
  const [trips, setTrips] = useState(null);
  const [plan, setPlan] = useState({ picks: {}, misses: {} });
  const [manual, setManual] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setTrips(null);
    try {
      const data = await boardTrips();
      const open = (data || []).filter(isOpen).sort((a, b) => String(a.scheduled_pickup).localeCompare(String(b.scheduled_pickup)));
      setTrips(open);
      if (!open.length) return;
      const p = await planByDay(open.map((t) => t.id), Object.fromEntries(open.map((t) => [t.id, t])));
      setPlan({
        picks: Object.fromEntries((p.assignments || []).map((a) => [a.trip_id, a])),
        misses: Object.fromEntries((p.unassignable || []).map((u) => [u.trip_id, u.reason])),
      });
    } catch (e) { toast.error(fleetError(e, "Couldn't load assign requests.")); setTrips((v) => v || []); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(true), 60000);

  const remove = (id) => {
    setTrips((prev) => prev.filter((t) => t.id !== id));
    window.dispatchEvent(new Event(REQUESTS_CHANGED));
  };

  if (!trips) return <Loading />;
  return (
    <div data-testid="requests-page">
      <PageHeader title="Assign Requests" subtitle={`${trips.length} open request${trips.length === 1 ? "" : "s"} from your team`}>
        <button onClick={() => load()} data-testid="requests-refresh"
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </PageHeader>
      <Panel testid="requests-list">
        {trips.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-5 py-12 text-center text-sm text-slate-400" data-testid="requests-empty">
            <Inbox className="h-6 w-6 text-slate-500" /> No open requests. When a dispatcher or supervisor taps “Ask owner to assign”, it shows up here.
          </div>
        ) : (
          <ul className="divide-y divide-slate-800">
            {trips.map((t) => <RequestRow key={t.id} t={t} pick={plan.picks[t.id]} miss={plan.misses[t.id]} onAssigned={remove} onManual={setManual} />)}
          </ul>
        )}
      </Panel>
      <ReassignDialog trip={manual} open={!!manual} onOpenChange={(v) => { if (!v) setManual(null); }} onReassigned={(id) => remove(id)} />
    </div>
  );
}
